"use client";

import {
  getCountActiveFilters,
  getFiltersUrlQuery,
  getResetFilters,
  ListingFilters,
  parseListingFilter,
  searchParamsToObject,
} from "@/lib/fetch/listing";
import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ListingMapPoint } from "@/lib/types/search";
import SearchFilters from "../../search/_components/filter/SearchFilters";
import MapFiltersWrapper from "./MapFiltersWrapper";
import { Badge } from "@/components/ui/badge";
import { useTranslations } from "next-intl";
import { AnimatePresence, motion } from "framer-motion";
import MapListingViewer from "./MapListingViewer";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";

const MapView = dynamic(() => import("./MapView"), {
  ssr: false,
});

const MotionBadge = motion(Badge);

type Props = {
  initialFilters: ListingFilters;
  locale: string;
};

type ListingMapResponseData = {
  items: ListingMapPoint[];
};

const WrapperMapContainer: React.FC<Props> = ({ initialFilters, locale }) => {
  const t = useTranslations("map");
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const searchParams = useSearchParams();
  const loadedInitialUrlFilters = useRef(false);
  const [currentGroupSelected, setCurrentGroupSelected] = useState<
    string[] | null
  >(null);
  const [groupIndex, setGroupIndex] = useState(0);
  const [filters, setFilters] = useState(initialFilters);
  const [canSyncUrl, setCanSyncUrl] = useState(false);
  const [countFilters, setCountFilters] = useState(
    getCountActiveFilters(filters),
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  // Runs on mount AND on real navigations (e.g. the city shortcuts below the
  // map linking to /map?city=X). Our own URL sync uses history.replaceState,
  // which does not re-render useSearchParams, so this cannot loop.
  useEffect(() => {
    loadedInitialUrlFilters.current = true;
    setFilters(parseListingFilter(searchParamsToObject(searchParams)));
    setCanSyncUrl(true);
  }, [searchParams]);

  useEffect(() => {
    if (!canSyncUrl) {
      return;
    }

    setCountFilters(getCountActiveFilters(filters));

    const { params } = getFiltersUrlQuery(filters);
    const nextSearch = params.toString();
    const currentSearch = window.location.search.slice(1);

    if (nextSearch !== currentSearch) {
      const nextUrl = `${window.location.pathname}${
        nextSearch ? `?${nextSearch}` : ""
      }`;

      window.history.replaceState(null, "", nextUrl);
    }
  }, [canSyncUrl, filters]);

  const { data, isLoading } = useQuery<ListingMapResponseData>({
    queryKey: ["map", "search", filters],
    enabled: canSyncUrl,
    staleTime: 1000 * 60 * 3,
    gcTime: 1000 * 60 * 15,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.append("locale", locale);

      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            params.append(key, v);
          });
        } else if (value) {
          params.append(key, value.toString());
        }
      });

      const response = await fetch(
        `/api/site/public/listing/map?${params.toString()}`,
        {
          method: "GET",
          signal,
        },
      );

      const payload = (await response.json()) as {
        success: boolean;
        data: ListingMapResponseData;
      };

      if (!response.ok || !payload.success) {
        throw new Error("Failed to fetch listing map points");
      }

      return payload.data;
    },
  });

  const onReset = () => {
    setFilters(getResetFilters());
  };

  // The map sits below the page header, so its bottom edge can start out
  // below the fold. When a listing panel opens, bring the whole map section
  // into view so the panel is never cut off.
  useEffect(() => {
    if (currentGroupSelected) {
      mapSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [currentGroupSelected]);

  return (
    <div
      ref={mapSectionRef}
      className="relative h-[70dvh] min-h-[480px] lg:h-[calc(100dvh-4.5rem)]"
    >
      <MapFiltersWrapper countFilters={countFilters}>
        <SearchFilters
          filters={filters}
          setFilters={(filters) => setFilters(filters)}
          onReset={onReset}
          countFilters={countFilters}
          includeSearch
        />
      </MapFiltersWrapper>
      {data && data.items.length === 0 && !isLoading && (
        <div className="absolute left-1/2 -translate-x-1/2 top-4 z-50">
          <MotionBadge
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            variant="secondary"
            className="border text-sm px-4 py-1.5 border-primary"
          >
            {t("no-results")}
          </MotionBadge>
        </div>
      )}
      {mounted && (
        <MapView
          fitKey={`${filters.search ?? ""}|${filters.city ?? ""}|${
            filters.province ?? ""
          }`}
          labels={{
            map: t("map"),
            toggleAttribution: t("toggle-attribution"),
            zoomIn: t("zoom-in"),
            zoomOut: t("zoom-out"),
            cooperativeZoom: t("cooperative-zoom"),
            cooperativeZoomMac: t("cooperative-zoom-mac"),
            cooperativeMove: t("cooperative-move"),
          }}
          points={data?.items || []}
          selectedListingId={currentGroupSelected?.[groupIndex] ?? null}
          hasSelection={currentGroupSelected !== null}
          onMarkerClick={(ids) => {
            setGroupIndex(0);
            setCurrentGroupSelected(ids);
          }}
        />
      )}

      <AnimatePresence>
        {currentGroupSelected && (
          <MapListingViewer
            key="map-listing-viewer"
            activeGroup={currentGroupSelected}
            onClose={() => setCurrentGroupSelected(null)}
            index={groupIndex}
            setIndex={setGroupIndex}
            locale={locale}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default WrapperMapContainer;
